import React, { ReactNode, useState } from 'react'
import classnames from 'classnames'
import { useTranslation } from 'react-i18next'
import {
    Label,
    Button,
} from 'reactstrap'

import { CheckUncheck } from '../../../components/shared/check-uncheck'
import Modal from '../../../components/shared/modal'
import EditValue from './edit-value'
import type { DeviceEntity } from './parse-device-data'
import Loader from '../../../components/shared/loader'
import { generateUUID } from '../../../components/shared/uuid-generator'
import {
    VOLTAGE_ALARM,
    ACTIVE_SERVICE,
    ACTIVE_LOW_OIL_ALARM,
    ACTIVE_STATUS,
    ACTIVE_PRESSURE_ALARM,
    ACTIVE_TIME_MODE,
    ACTIVE_SERVICE_MODE,
    ACTIVE_REDUCED_DOSAGE,
    ACTIVE_DIAGNOSTIC_MENU,
    ACTIVE_PRESSURE_1_ERROR,
    ACTIVE_PRESSURE_2_ERROR,
    PASSAGE_COUNTER,
    ACTIVE_WHITE_BACKLIGHT,
    ACTIVE_RED_BACKLIGHT,
    REM_CHANGE_REGIME,
    REM_LOWER_DOSAGE,
    REM_HIGHER_DOSAGE,
    LIGHT,
    PUMP,
    SIREN,
    OIL_LEVEL,
    VOLTAGE,
    INDICATION_ACTIVITY,
    INDICATION_SENSOR_MODE,
    INDICATION_TIME_MODE,
    INDICATION_PUMP,
    INDICATION_SERVICE,
    INDICATION_TRANSIT,
    INDICATION_LOWER_DOSAGE,
    INDICATION_HIGHER_DOSAGE,
    SWITCH_ACTIVE,
    SWITCH_PUMP_MODE,
    SWITCH_OFF_PEAK_MODE,
    SWITCH_HIGH_PEAK_MODE,
} from '../../../components/shared/device-addresses'

interface DeviceData {
    address: string
    hint: string
    data: string
    type?: string
}

interface MonitoredStateProps {
    device: DeviceEntity
    deviceData: DeviceData
    deviceWriter: string[]
    className?: string
    children?: ReactNode
}

interface EditDataModalState {
    device: DeviceEntity
    deviceData: DeviceData
}

/**
 * Renders a single row in the monitored device state grid.
 *
 * Each row maps a device data hint to a presentational widget
 * (checkbox, alarm pill, label etc.) and may show an edit modal.
 */
const DeviceMonitoredState = ({
    device,
    deviceData,
    deviceWriter,
    className,
    children,
}: MonitoredStateProps) => {
    const { t } = useTranslation()
    const [editDataModal, setEditDataModal] = useState<EditDataModalState | null>(null)

    const renderCheckbox = (label: string) => {
        const checkUid = generateUUID()

        return (
            <div className="form-check">
                <input
                    type="checkbox"
                    className="form-check-input"
                    id={checkUid}
                    value={String(!!deviceData.data)}
                />
                <label className="form-check-label" htmlFor={checkUid}>
                    {label}
                </label>
            </div>
        )
    }

    const renderCheckUncheck = (condition: boolean) => (
        <>
            {t(`deviceData.hint.${deviceData.hint}`)}:{' '}
            <CheckUncheck success={condition} />
        </>
    )

    const renderAlarmStatus = (condition: boolean) => (
        <div className="d-flex justify-content-between align-items-center border-bottom p-3">
            {t(`deviceData.hint.${deviceData.hint}`)}
            <i
                className={classnames({
                    'fas fa-circle mr-3 ml-1': true,
                    'text-danger': condition,
                    'text-success': !condition,
                })}
            />
        </div>
    )

    const renderFormInput = (title: string, data: string, measurement: string) => (
        <div className="d-flex flex-column w-100 mb-4">
            <Label>{title}</Label>
            <Button
                color="falcon-default"
                onClick={() => setEditDataModal({ device, deviceData })}
            >
                {deviceWriter.includes(deviceData.address) ? (
                    <Loader className="ml-2" height={18} />
                ) : (
                    <div className="d-flex justify-content-between">
                        <span>{data}</span>
                        <span>{measurement}</span>
                    </div>
                )}
            </Button>
        </div>
    )

    const renderStatus = (): ReactNode => {
        switch (deviceData.hint) {
            case VOLTAGE_ALARM:
            case ACTIVE_SERVICE_MODE:
            case ACTIVE_LOW_OIL_ALARM:
            case ACTIVE_PRESSURE_ALARM:
            case ACTIVE_RED_BACKLIGHT:
                return renderAlarmStatus(deviceData.data !== '0')

            case ACTIVE_STATUS:
                return renderAlarmStatus(deviceData.data === '0')

            case ACTIVE_TIME_MODE:
            case ACTIVE_SERVICE:
            case ACTIVE_REDUCED_DOSAGE:
            case ACTIVE_DIAGNOSTIC_MENU:
                return renderAlarmStatus(deviceData.data !== '0')

            case ACTIVE_PRESSURE_1_ERROR:
            case ACTIVE_PRESSURE_2_ERROR:
                return renderAlarmStatus(deviceData.data === '1')

            case PASSAGE_COUNTER:
                return (
                    <>
                        {t('deviceData.hint.PASSAGE_COUNTER')}: <b>{deviceData.data}</b>
                    </>
                )
            case ACTIVE_WHITE_BACKLIGHT:
            case REM_CHANGE_REGIME:
            case REM_LOWER_DOSAGE:
            case REM_HIGHER_DOSAGE:
            case LIGHT:
            case PUMP:
            case SIREN:
            case INDICATION_PUMP:
                return null
            case OIL_LEVEL:
                return (
                    <>
                        {t('deviceData.hint.OIL_LEVEL')}: <b>{deviceData.data} %</b>
                    </>
                )
            case VOLTAGE:
                return (
                    <>
                        {t('deviceData.hint.VOLTAGE')}: <b>{Number(deviceData.data) / 1000} V</b>
                    </>
                )
            case INDICATION_ACTIVITY:
                return (
                    <>
                        {t('deviceData.hint.INDICATION_ACTIVITY')}: <b>{deviceData.data}</b>
                    </>
                )
            case INDICATION_SENSOR_MODE:
                return (
                    <>
                        {t('deviceData.hint.INDICATION_SENSOR_MODE')}: <b>{deviceData.data}</b>
                    </>
                )
            case INDICATION_TIME_MODE:
                return (
                    <>
                        {t('deviceData.hint.INDICATION_TIME_MODE')}: <b>{deviceData.data}</b>
                    </>
                )
            case INDICATION_SERVICE:
                return (
                    <>
                        {t('deviceData.hint.INDICATION_SERVICE')}: <b>{deviceData.data}</b>
                    </>
                )
            case INDICATION_TRANSIT:
                return (
                    <>
                        {t('deviceData.hint.INDICATION_TRANSIT')}: {deviceData.data}
                    </>
                )
            case INDICATION_LOWER_DOSAGE:
                return (
                    <>
                        {t('deviceData.hint.INDICATION_LOWER_DOSAGE')}: {deviceData.data}
                    </>
                )
            case INDICATION_HIGHER_DOSAGE:
                return (
                    <>
                        {t('deviceData.hint.INDICATION_HIGHER_DOSAGE')}: {deviceData.data}
                    </>
                )
            case SWITCH_ACTIVE:
            case SWITCH_PUMP_MODE:
            case SWITCH_OFF_PEAK_MODE:
            case SWITCH_HIGH_PEAK_MODE:
                return renderCheckbox(t(`deviceData.hint.${deviceData.hint}`))
            default:
                return null
        }
    }

    const renderedStatus = renderStatus()

    if (!renderedStatus) {
        return null
    }

    // Re-expose helpers so we can keep `renderCheckUncheck` / `renderFormInput`
    // available to local switch branches in future refactors.
    void renderCheckUncheck
    void renderFormInput

    return (
        <div
            className={classnames({
                [className ?? '']: !!className,
            })}
        >
            {renderedStatus}
            {children}
            {editDataModal && (
                <Modal
                    description={t('deviceDetail.edit')}
                    handleHideModal={() => setEditDataModal(null)}
                    content={() => (
                        <EditValue
                            editDataModal={editDataModal}
                            onSuccess={() => setEditDataModal(null)}
                        />
                    )}
                />
            )}
        </div>
    )
}

export default DeviceMonitoredState
